import { FormControlLabel, FormGroup, Switch, Typography } from '@mui/material';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { MultipleAutocompleteAdapter } from 'components/enhanced/form/adapters/selects/MultipleAutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from 'shared/types/general';
import { IInventoryCheck } from '../types';
import { supplierService } from 'features/inventory/suppliers/api/SupplierService';
import { locationService, categoryService } from 'shared/services';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';

interface InventoryCheckFormProps {
  inventory: IInventoryCheck;
  onSubmit: any;
}

const criteria = [
  { id: 1, code: 'supplier', description: 'Fornitore' },
  { id: 2, code: 'location', description: 'Piazza' },
  { id: 3, code: 'category', description: 'Categoria' }
];

function InventoryCheckForm({ inventory, onSubmit }: InventoryCheckFormProps) {
  const { t } = useTranslation();

  const [partial, setPartial] = useState(inventory?.partial);
  const [warehouses, setWarehouses] = useState<Option[]>([]);
  const [criterion, setCriterion] = useState<Option | null>(null);
  const [options, setOptions] = useState<Option[]>([]);

  useEffect(() => {
    if (inventory?.partial) setPartial(inventory.partial);
  }, [inventory?.partial]);

  useEffect(() => {
    switch (criterion?.code) {
      case 'supplier':
        inventory.criterion = criterion.id;
        supplierService.getAllBaseInformation().then((res) => {
          setOptions(
            res.map((item) => {
              return {
                id: item.id,
                code: `${item.code} - ${item.company_name}`
              };
            })
          );
        });
        break;
      case 'location':
        inventory.criterion = criterion.id;
        locationService.getAllBaseInformation().then((res) => {
          setOptions(
            res.map((item) => {
              return {
                id: item.id,
                code: item.code
              };
            })
          );
        });
        break;
      case 'category':
        inventory.criterion = criterion.id;
        categoryService.getAllBaseInformation().then((res) => {
          setOptions(
            res.map((item) => {
              return {
                id: item.id,
                code: item.code
              };
            })
          );
        });
        break;
      default:
        setOptions([]);
    }
  }, [criterion?.id]);

  const handleSubmit = (values) => {
    switch (criterion?.code) {
      case 'supplier':
        delete values['location'];
        delete values['category'];
        break;
      case 'location':
        delete values['supplier'];
        delete values['category'];
        break;
      case 'category':
        delete values['supplier'];
        delete values['location'];
        break;
    }
    values['partial'] = partial;
    onSubmit(values);
  };

  useEffect(() => {
    warehouseService.getAllBaseInformation().then(setWarehouses);
  }, []);

  const togglePartial = () => {
    setPartial((prevState) => !prevState);
  };

  return (
    <div>
      <div className="bg-gray-50 border border-gray-100 rounded-xl px-6 py-3 mb-3 shadow-sm hover:shadow-md transition-shadow duration-200">
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={partial}
                onChange={togglePartial}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            }
            label={
              <Typography variant="body2" className="text-sm font-semibold text-gray-700 ml-1">
                <b>{t('global.partial-inventory')}</b>
              </Typography>
            }
          />
        </FormGroup>
      </div>
      {inventory && (
        <EnhancedForm onSubmit={handleSubmit} edit={!!inventory.id} autoStyleLayout columns={3}>
          <IdentityField
            name="warehouse"
            component={AutocompleteAdapter}
            initValue={warehouses.find((w) => w.code == inventory.warehouse)}
            options={warehouses}
            optionValue="code"
            label={t('global.warehouse')}
          />

          {partial && (
            <>
              <IdentityField
                name="criterion"
                component={AutocompleteAdapter}
                initValue={criteria.find((o) => o.id == inventory?.criterion)}
                options={criteria}
                optionLabel="description"
                label={t('global.criterion')}
                setValue={setCriterion}
              />

              {criterion?.code === 'supplier' && (
                <IdentityField
                  name="suppliers"
                  component={MultipleAutocompleteAdapter}
                  options={options}
                  initValue={inventory.suppliers}
                  label={t('global.suppliers')}
                  type="text"
                />
              )}
              {criterion?.code === 'location' && (
                <IdentityField
                  name="locations"
                  component={MultipleAutocompleteAdapter}
                  options={options}
                  initValue={inventory.locations}
                  label={t('global.locations')}
                  type="text"
                />
              )}
              {criterion?.code === 'category' && (
                <IdentityField
                  name="categories"
                  component={MultipleAutocompleteAdapter}
                  options={options}
                  initValue={inventory.categories}
                  label={t('global.categories')}
                  type="text"
                />
              )}
            </>
          )}

          <IdentityField
            gridSpan={3}
            name="notes"
            component={TextFieldAdapter}
            label={t('global.description')}
            type="text"
            initialValue={inventory.notes}
          />
        </EnhancedForm>
      )}
    </div>
  );
}

export default InventoryCheckForm;
